import React, { useState } from 'react';
import {
  Card,
  Select,
  List,
  Tag,
  Button,
  Empty,
  Statistic,
  Row,
  Col,
  message,
  Progress,
  Divider,
} from 'antd';
import {
  WarningOutlined,
  BookOutlined,
  CopyOutlined,
  ArrowRightOutlined,
  FireOutlined,
} from '@ant-design/icons';
import { Space } from 'antd';

const { Option } = Select;

// 法规推荐数据
interface LawRecommendation {
  id: string;
  name: string;
  article: string;
  content: string;
  citationCount: number;
  successRate: number;
  replyPositions: { position: string; count: number }[];
}

// 风险点数据
const riskPoints = [
  '超越资质范围',
  '排他性条款',
  '低于成本价',
  '参数歧义',
  '业绩要求',
  '付款方式',
  '工期要求',
  '人员资格',
];

// 模拟法规推荐数据
const mockLawRecommendations: Record<string, LawRecommendation[]> = {
  '超越资质范围': [
    {
      id: 'law1',
      name: '中华人民共和国建筑法',
      article: '第十三条',
      content: '从事建筑活动的建筑施工企业、勘察单位、设计单位和工程监理单位，按照其拥有的注册资本、专业技术人员、技术装备和已完成的建筑工程业绩等资质条件，划分为不同的资质等级，经资质审查合格，取得相应等级的资质证书后，方可在其资质等级许可的范围内从事建筑活动。',
      citationCount: 45,
      successRate: 78,
      replyPositions: [
        { position: '不采纳', count: 35 },
        { position: '部分采纳', count: 8 },
        { position: '澄清说明', count: 2 },
      ],
    },
    {
      id: 'law2',
      name: '烟草建筑消防设计规范',
      article: 'DBJ53-13-2012',
      content: '烟草建筑生产及储存的火灾危险性类别为丙类，消防设施工程专业承包二级资质可承包范围为火灾危险性丙类以下的厂房、仓库、储罐、堆场。',
      citationCount: 32,
      successRate: 85,
      replyPositions: [
        { position: '不采纳', count: 28 },
        { position: '部分采纳', count: 4 },
      ],
    },
    {
      id: 'law3',
      name: '中华人民共和国招标投标法实施条例',
      article: '第三十二条',
      content: '招标人不得以不合理的条件限制、排斥潜在投标人或者投标人。',
      citationCount: 28,
      successRate: 65,
      replyPositions: [
        { position: '部分采纳', count: 15 },
        { position: '不采纳', count: 10 },
        { position: '澄清说明', count: 3 },
      ],
    },
  ],
  '排他性条款': [
    {
      id: 'law4',
      name: '中华人民共和国招标投标法',
      article: '第十八条',
      content: '招标人不得以不合理的条件限制或者排斥潜在投标人，不得对潜在投标人实行歧视待遇。',
      citationCount: 38,
      successRate: 72,
      replyPositions: [
        { position: '部分采纳', count: 20 },
        { position: '澄清说明', count: 12 },
        { position: '不采纳', count: 6 },
      ],
    },
    {
      id: 'law5',
      name: '中华人民共和国招标投标法实施条例',
      article: '第三十二条',
      content: '招标人有下列行为之一的，属于以不合理条件限制、排斥潜在投标人或者投标人...',
      citationCount: 42,
      successRate: 68,
      replyPositions: [
        { position: '部分采纳', count: 22 },
        { position: '澄清说明', count: 14 },
        { position: '不采纳', count: 6 },
      ],
    },
  ],
  '低于成本价': [
    {
      id: 'law6',
      name: '评标委员会和评标方法暂行规定',
      article: '第二十一条',
      content: '在评标过程中，评标委员会发现投标人的报价明显低于其他投标报价，或者在设有标底时明显低于标底，使得其投标报价可能低于其个别成本的，应当要求该投标人作出书面说明并提供相关证明材料。',
      citationCount: 56,
      successRate: 82,
      replyPositions: [
        { position: '不采纳', count: 40 },
        { position: '部分采纳', count: 12 },
        { position: '澄清说明', count: 4 },
      ],
    },
  ],
};

const SmartRegulation: React.FC = () => {
  const [selectedRiskPoint, setSelectedRiskPoint] = useState<string>('超越资质范围');
  const [recommendations, setRecommendations] = useState<LawRecommendation[]>(
    mockLawRecommendations['超越资质范围'] || []
  );

  // 选择风险点
  const handleRiskPointChange = (value: string) => {
    setSelectedRiskPoint(value);
    setRecommendations(mockLawRecommendations[value] || []);
  };

  // 复制法规引用
  const handleCopyCitation = (law: LawRecommendation) => {
    const citation = `《${law.name}》${law.article}: ${law.content.slice(0, 100)}...`;
    navigator.clipboard.writeText(citation);
    message.success('法规引用已复制');
  };

  // 获取结论颜色
  const getPositionColor = (position: string) => {
    const colors: Record<string, string> = {
      '不采纳': 'red',
      '部分采纳': 'orange',
      '完全采纳': 'green',
      '澄清说明': 'blue',
    };
    return colors[position] || 'default';
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 顶部选择区 */}
      <Card className="mb-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <WarningOutlined className="text-red-500" />
            <span className="font-medium">选择风险点:</span>
          </div>
          <Select
            value={selectedRiskPoint}
            onChange={handleRiskPointChange}
            style={{ width: 300 }}
           
          >
            {riskPoints.map((rp) => (
              <Option key={rp} value={rp}>
                <Tag color="red">{rp}</Tag>
              </Option>
            ))}
          </Select>
          <div className="text-gray-400 text-sm">
            共找到 {recommendations.length} 条相关法规
          </div>
        </div>
      </Card>

      {/* 主体内容 */}
      <Row gutter={16} className="flex-1 min-h-0 overflow-hidden">
        {/* 左侧：桑基图示意 + 统计 */}
        <Col span={8} className="h-full overflow-auto">
          <Card title="处理流向分析" className="h-full" styles={{ body: { height: 'calc(100% - 46px)', overflow: 'auto' } }}>
            {recommendations.length > 0 ? (
              <div>
                {/* 桑基图示意 */}
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <div className="text-center mb-4">
                    <Tag color="red" className="text-base px-3 py-1">
                      <WarningOutlined /> {selectedRiskPoint}
                    </Tag>
                  </div>
                  
                  <div className="flex justify-center mb-4">
                    <ArrowRightOutlined className="text-2xl text-gray-400" />
                  </div>

                  <div className="space-y-2">
                    {recommendations.slice(0, 3).map((law) => (
                      <div key={law.id} className="flex items-center gap-2">
                        <div className="flex-1 text-center">
                          <Tag color="blue">
                            <BookOutlined /> {law.name.slice(0, 10)}...
                          </Tag>
                        </div>
                        <div className="text-xs text-gray-400">
                          引用{law.citationCount}次
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center my-4">
                    <ArrowRightOutlined className="text-2xl text-gray-400" />
                  </div>

                  <div className="flex justify-center gap-2">
                    <Tag color="red">不采纳</Tag>
                    <Tag color="orange">部分采纳</Tag>
                    <Tag color="blue">澄清说明</Tag>
                  </div>
                </div>

                {/* 统计 */}
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Card className="text-center">
                      <Statistic
                        title="总引用次数"
                        value={recommendations.reduce((sum, r) => sum + r.citationCount, 0)}
                        prefix={<FireOutlined className="text-red-500" />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card className="text-center">
                      <Statistic
                        title="平均成功率"
                        value={Math.round(
                          recommendations.reduce((sum, r) => sum + r.successRate, 0) /
                            recommendations.length
                        )}
                        suffix="%"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Divider />

                {/* 处理结果分布 */}
                <div>
                  <div className="text-sm font-medium mb-2">处理结果分布:</div>
                  {['不采纳', '部分采纳', '澄清说明'].map((position) => {
                    const count = recommendations.reduce(
                      (sum, r) =>
                        sum + (r.replyPositions.find((p) => p.position === position)?.count || 0),
                      0
                    );
                    const total = recommendations.reduce(
                      (sum, r) => sum + r.replyPositions.reduce((s, p) => s + p.count, 0),
                      0
                    );
                    const percent = total > 0 ? Math.round((count / total) * 100) : 0;

                    return (
                      <div key={position} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <Tag color={getPositionColor(position)}>{position}</Tag>
                          <span>{count}次 ({percent}%)</span>
                        </div>
                        <Progress percent={percent} showInfo={false} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Empty description="暂无数据" />
            )}
          </Card>
        </Col>

        {/* 右侧：法规推荐列表 */}
        <Col span={16} className="h-full overflow-auto">
          <Card
            title={
              <div className="flex items-center justify-between">
                <span>
                  <BookOutlined className="mr-2" />
                  法规推荐列表
                </span>
                <Tag color="blue">Top {recommendations.length}</Tag>
              </div>
            }
           
            className="h-full"
            styles={{ body: { height: 'calc(100% - 46px)', overflow: 'auto' } }}
          >
            <List
              itemLayout="vertical"
              dataSource={recommendations}
              renderItem={(item, index) => (
                <List.Item
                  key={item.id}
                  actions={[
                    <Button
                      key="copy"
                      type="primary"
                     
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyCitation(item)}
                    >
                      复制引用
                    </Button>,
                  ]}
                  extra={
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        #{index + 1}
                      </div>
                      <div className="text-xs text-gray-400">推荐排名</div>
                    </div>
                  }
                >
                  <List.Item.Meta
                    title={
                      <div className="flex items-center gap-2">
                        <BookOutlined className="text-blue-500" />
                        <span className="font-bold">《{item.name}》</span>
                        <Tag color="blue">{item.article}</Tag>
                      </div>
                    }
                    description={
                      <div className="mt-2">
                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 mb-3">
                          {item.content}
                        </div>
                        
                        <Row gutter={16}>
                          <Col span={8}>
                            <Statistic
                              title="引用频次"
                              value={item.citationCount}
                              prefix={<FireOutlined className="text-red-500" />}
                              valueStyle={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic
                              title="反驳成功率"
                              value={item.successRate}
                              suffix="%"
                              valueStyle={{
                                fontSize: 16,
                                color:
                                  item.successRate >= 80
                                    ? '#52c41a'
                                    : item.successRate >= 60
                                    ? '#faad14'
                                    : '#f5222d',
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <div className="text-xs text-gray-500 mb-1">处理结果:</div>
                            <Space wrap>
                              {item.replyPositions.map((pos) => (
                                <Tag
                                  key={pos.position}
                                  color={getPositionColor(pos.position)}
                                  className="text-xs"
                                >
                                  {pos.position}: {pos.count}
                                </Tag>
                              ))}
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SmartRegulation;
